import {
  browseBranchDeliveryMethods,
  getRestaurantSettings,
} from "@aitisal/sdk/restaurant";
import { Button } from "@aitisal/ui/retail-storefront/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTrigger,
} from "@aitisal/ui/retail-storefront/drawer";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Minus, Plus, ShoppingCart, Trash2, Truck, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { getCustomizationDisplay, getTitle } from "../../helper/helper";
import { getVariantDisplay } from "../../helper/variantHelper";
import { useMenuAggregate } from "../../hooks/useMenuAggregate";
import { useSession } from "../../stores/sessionStore";
import { useCart } from "../../stores/useCart";

const ShoppingCartDrawer: React.FC = () => {
  const navigate = useNavigate();
  const { restaurantId, restaurantAreaId } = useSession();

  const [isOpen, setIsOpen] = useState(false);
  const [confirmingClear, setConfirmingClear] = useState(false);

  const {
    cart,
    totalPrice,
    onIncrement,
    onDecrement,
    removeFromCart,
    clearCart,
  } = useCart();

  const totalItems = cart.reduce(
    (sum: number, item: any) => sum + (item.quantity || 1),
    0,
  );

  useEffect(() => {
    if (!confirmingClear) return;
    const timer = setTimeout(() => setConfirmingClear(false), 3500);
    return () => clearTimeout(timer);
  }, [confirmingClear]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) setConfirmingClear(false);
  };

  const handleClearCart = () => {
    clearCart();
    setConfirmingClear(false);
  };

  const { data: menusData } = useMenuAggregate();

  const { data: restaurantSettings } = useQuery({
    queryKey: ["restaurant-settings", restaurantId],
    queryFn: async () => {
      if (!restaurantId) return null;
      const response = await getRestaurantSettings(restaurantId);
      return response.data;
    },
    enabled: Boolean(restaurantId),
  });

  const { data: branchDeliveryMethods } = useQuery<any>({
    queryKey: ["branch-delivery-methods", restaurantId, restaurantAreaId],
    queryFn: async () => {
      if (!restaurantId || !restaurantAreaId) return null;
      const response = await browseBranchDeliveryMethods(
        restaurantId,
        restaurantAreaId,
      );
      return response.data;
    },
    enabled: Boolean(restaurantId) && Boolean(restaurantAreaId),
  });

  const subtotal = totalPrice / 100;
  const fallbackDeliveryFee =
    (restaurantSettings?.delivery?.deliveryFee ?? 0) / 100;
  const areaDeliveryPriceCents = branchDeliveryMethods?.items?.[0]?.price;
  const deliveryFee =
    typeof areaDeliveryPriceCents === "number"
      ? areaDeliveryPriceCents / 100
      : fallbackDeliveryFee;
  const freeDeliveryThresholdCents =
    restaurantSettings?.delivery?.freeDeliveryThreshold ?? 0;
  const freeDeliveryThreshold = freeDeliveryThresholdCents / 100;
  const minOrderAmount =
    (restaurantSettings?.delivery?.minOrderAmount ?? 0) / 100;
  const belowMinOrder = minOrderAmount > 0 && subtotal < minOrderAmount;
  const freeDeliveryEligible =
    freeDeliveryThreshold > 0 && subtotal >= freeDeliveryThreshold;
  const deliveryCharges = freeDeliveryEligible ? 0 : deliveryFee;
  const estimatedTotal = subtotal + deliveryCharges;

  let nudge: {
    tone: "warning" | "brand" | "success";
    label: string;
    pct: number;
  } | null = null;
  if (belowMinOrder) {
    nudge = {
      tone: "warning",
      label: `Add Rs. ${(minOrderAmount - subtotal).toFixed(2)} for minimum order`,
      pct: (totalPrice / (minOrderAmount * 100)) * 100,
    };
  } else if (freeDeliveryThreshold > 0 && !freeDeliveryEligible) {
    nudge = {
      tone: "brand",
      label: `Rs. ${(freeDeliveryThreshold - subtotal).toFixed(2)} to free delivery`,
      pct: (totalPrice / freeDeliveryThresholdCents) * 100,
    };
  } else if (freeDeliveryEligible) {
    nudge = { tone: "success", label: "Free delivery unlocked", pct: 100 };
  }

  const formatPrice = (amountInCents: number) =>
    (amountInCents / 100).toFixed(2);

  return (
    <>
      <style>{`
        .force-right-drawer {
          inset: 0 0 0 auto !important;
          width: 100% !important;
          max-width: 420px !important;
          height: 100dvh !important;
          margin-top: 0 !important;
          border-radius: 0 !important;
          outline: none !important;
          transform: translateX(100%) !important;
          transition: transform 400ms cubic-bezier(0.32, 0.72, 0, 1) !important;
          will-change: transform;
        }

        [data-state="open"].force-right-drawer {
          transform: translateX(0%) !important;
        }

        [data-state="closed"].force-right-drawer {
          transform: translateX(100%) !important;
        }

        @media (max-width: 768px) {
          .force-right-drawer {
            inset: 0 !important;
            width: 100vw !important;
            max-width: none !important;
            transform: translateX(100%) !important;
          }

          [data-state="open"].force-right-drawer {
            transform: translateX(0%) !important;
          }

          [data-state="closed"].force-right-drawer {
            transform: translateX(100%) !important;
          }
        }

        [data-vaul-overlay] {
          background-color: rgba(0, 0, 0, 0.6) !important; 
          z-index: 550 !important;
        }

        .force-right-drawer > div:first-child {
          display: none !important;
        }
        
        [data-vaul-drawer] {
          touch-action: none !important;
        }
      `}</style>

      {/* 2. BIND STATE TO DRAWER COMPONENT */}
      <Drawer
        direction="right"
        dismissible={true}
        open={isOpen}
        onOpenChange={handleOpenChange}
      >
        <DrawerTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="p-3 pl-0 h-auto w-auto rounded-full transition relative outline-none cursor-pointer group hover:bg-transparent"
            aria-label={`Open cart, ${totalItems} ${totalItems === 1 ? "item" : "items"}`}
          >
            <ShoppingCart className="w-5 h-5 text-gray-600 group-hover:text-[var(--brand-color)]" />

            {cart.length > 0 && (
              <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center text-[10px] font-bold bg-[var(--background-branding)] text-white rounded-full border-2 border-white shadow-sm z-10">
                {totalItems}
              </span>
            )}
          </Button>
        </DrawerTrigger>

        <DrawerContent
          className="force-right-drawer z-[600] flex flex-col bg-white border-l shadow-2xl"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-bold text-gray-900">
                    Shopping Cart
                  </h2>
                  {totalItems > 0 && (
                    <span className="bg-[var(--brand-color)] text-white text-xs font-medium px-2.5 py-0.5 rounded-full">
                      {totalItems}
                    </span>
                  )}
                </div>
                <DrawerClose asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="p-2 h-auto w-auto rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <X className="h-5 w-5 text-gray-500" />
                  </Button>
                </DrawerClose>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto bg-white">
              {cart.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-20 px-6">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <ShoppingCart className="h-8 w-8 text-gray-300" />
                  </div>
                  <p className="text-lg font-medium text-gray-900">
                    Your cart is empty
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Browse the catalogue and find something you love
                  </p>
                  <Button
                    className="mt-6 h-11 px-6 rounded-xl bg-[var(--brand-color)] text-white font-semibold hover:opacity-90 cursor-pointer"
                    onClick={() => {
                      setIsOpen(false);
                      navigate({ to: "/c" });
                    }}
                  >
                    Start Shopping
                  </Button>
                </div>
              ) : (
                <div className="px-6 pt-8 pb-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-medium uppercase tracking-wider text-gray-400">
                      {totalItems} {totalItems === 1 ? "item" : "items"}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        confirmingClear
                          ? handleClearCart()
                          : setConfirmingClear(true)
                      }
                      className={`text-xs font-medium cursor-pointer transition-colors ${
                        confirmingClear
                          ? "text-red-600"
                          : "text-gray-400 hover:text-gray-700"
                      }`}
                    >
                      {confirmingClear ? "Tap again to clear" : "Clear all"}
                    </button>
                  </div>
                  <div className="space-y-3">
                    {cart.map((item: any) => {
                      const customizationNames: string[] =
                        item.customizations && item.customizations.length > 0
                          ? getCustomizationDisplay(item, menusData)
                          : [];
                      const variantNames: string[] = getVariantDisplay(
                        item,
                        menusData,
                      );
                      const optionsText = [
                        ...variantNames,
                        ...customizationNames,
                      ]
                        .filter(Boolean)
                        .join(" · ");
                      const itemName = getTitle(item.name);

                      return (
                        <div
                          key={item.hashId}
                          className="flex gap-3.5 p-3.5 bg-white border border-gray-100 rounded-2xl shadow-sm transition-all duration-200 hover:shadow-md hover:border-gray-200"
                        >
                          <img
                            src={
                              item.images?.[0]?.thumbnailImage ||
                              "/images/img-placeholder.svg"
                            }
                            alt={itemName}
                            className="w-[72px] h-[72px] object-cover rounded-xl flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0 flex flex-col">
                            <div className="flex items-start justify-between gap-3">
                              <h3 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-1">
                                {itemName}
                              </h3>
                              <span className="text-sm font-bold text-gray-900 whitespace-nowrap">
                                Rs. {formatPrice(item.price * item.quantity)}
                              </span>
                            </div>

                            {optionsText && (
                              <p className="mt-1 text-xs text-gray-500 leading-snug line-clamp-2">
                                {optionsText}
                              </p>
                            )}

                            <div className="mt-auto pt-2.5 flex items-center justify-between gap-2">
                              <div className="flex items-center gap-0.5 bg-gray-100 rounded-full p-1">
                                <button
                                  type="button"
                                  aria-label={
                                    item.quantity === 1
                                      ? `Remove ${itemName} from cart`
                                      : `Decrease quantity of ${itemName}`
                                  }
                                  className="size-6 flex items-center justify-center rounded-full text-gray-600 hover:bg-white hover:shadow-sm transition-all cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (item.quantity > 1) {
                                      onDecrement(item.hashId);
                                    } else {
                                      removeFromCart(item.hashId);
                                    }
                                  }}
                                >
                                  {item.quantity === 1 ? (
                                    <Trash2 className="h-3.5 w-3.5 text-gray-500" />
                                  ) : (
                                    <Minus className="h-3.5 w-3.5" />
                                  )}
                                </button>
                                <span className="text-xs font-semibold text-gray-900 w-6 text-center tabular-nums">
                                  {item.quantity}
                                </span>
                                <button
                                  type="button"
                                  aria-label={`Increase quantity of ${itemName}`}
                                  className="size-6 flex items-center justify-center rounded-full text-gray-600 hover:bg-white hover:shadow-sm transition-all cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onIncrement(item.hashId);
                                  }}
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                </button>
                              </div>
                              {item.quantity > 1 && (
                                <span className="text-[11px] text-gray-400 whitespace-nowrap">
                                  Rs. {formatPrice(item.price)} each
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {cart.length > 0 && (
              <div className="border-t border-gray-100 px-5 py-4 space-y-2 bg-white">
                {nudge && (
                  <div className="flex items-center gap-2 pb-0.5">
                    <Truck
                      className={`size-3.5 shrink-0 ${
                        nudge.tone === "warning"
                          ? "text-amber-500"
                          : nudge.tone === "success"
                            ? "text-green-600"
                            : "text-[var(--brand-color)]"
                      }`}
                    />
                    <div className="flex-1 h-1 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${
                          nudge.tone === "warning"
                            ? "bg-amber-500"
                            : nudge.tone === "success"
                              ? "bg-green-500"
                              : "bg-[var(--brand-color)]"
                        }`}
                        style={{ width: `${Math.min(nudge.pct, 100)}%` }}
                      />
                    </div>
                    <span
                      className={`text-[11px] font-medium whitespace-nowrap ${
                        nudge.tone === "warning"
                          ? "text-amber-600"
                          : nudge.tone === "success"
                            ? "text-green-600"
                            : "text-gray-600"
                      }`}
                    >
                      {nudge.label}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="font-medium text-gray-900">
                    Rs. {formatPrice(totalPrice)}
                  </span>
                </div>
                {freeDeliveryEligible ? (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Delivery</span>
                    <span className="flex items-center gap-1.5">
                      {deliveryFee > 0 && (
                        <span className="text-gray-400 line-through">
                          Rs. {deliveryFee.toFixed(2)}
                        </span>
                      )}
                      <span className="font-semibold text-green-600">FREE</span>
                    </span>
                  </div>
                ) : deliveryCharges > 0 ? (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Delivery</span>
                    <span className="font-medium text-gray-900">
                      Rs. {deliveryCharges.toFixed(2)}
                    </span>
                  </div>
                ) : null}
                <div className="flex items-center justify-between border-t border-dashed border-gray-200 pt-2">
                  <span className="text-sm font-semibold text-gray-900">
                    Estimated Total
                  </span>
                  <span className="text-base font-bold text-gray-900">
                    Rs. {estimatedTotal.toFixed(2)}
                  </span>
                </div>
                <Button
                  className="w-full h-11 text-[15px] bg-[var(--brand-color)] hover:opacity-90 text-white font-semibold rounded-xl cursor-pointer transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={belowMinOrder}
                  onClick={() => {
                    setIsOpen(false);
                    navigate({ to: "/c/checkout" });
                  }}
                >
                  {belowMinOrder
                    ? `Minimum Rs. ${minOrderAmount.toFixed(2)} Required`
                    : `Checkout · Rs. ${estimatedTotal.toFixed(2)}`}
                </Button>
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default ShoppingCartDrawer;
